/**
 * GENERATED FILE — DO NOT EDIT.
 *
 * Source:    config/catalog/preisspiegel@v1.yaml
 * Generator: frontend/scripts/generate-preisspiegel-catalog.mjs
 * Regenerate with `npm run codegen` (also runs on `prestart` / `prebuild`).
 *
 * Edits made here are silently discarded on the next build, and
 * `__tests__/catalogCodegen.test.ts` reds CI while they survive. To change the
 * catalog — add a tile, rename one, add a unit — edit the YAML and regenerate;
 * nothing else in `frontend/src` needs to know (#2771).
 *
 * Shape: Warengruppe (L1) → Kategorie (L2) → tracked item (L3). Pure data,
 * zero imports. Kept import-free deliberately: the Deckungsbeitrag drawer
 * reaches across an app boundary for `findItem` (#2538), so this module must
 * never be able to pull React or the data-plane along with it, and must never
 * take part in a module cycle.
 *
 * Helpers (`allItems`, `findItem`, `findKategorie`, `validateCatalog`) are NOT
 * generated — they are hand-written in `./catalog`, which re-exports
 * everything below. Consumers import `./catalog`, never this file.
 */

/**
 * Price units the catalog can quote.
 *
 * ⚠️ NOT interchangeable, and the 10x €/t ↔ €/dt gap sits INSIDE one
 * Warengruppe (Marktfrüchte is mostly €/t, but Kartoffeln is €/dt). The twin
 * stores arable yield in **dt**, so anything multiplying a yield by one of
 * these MUST branch on the item's declared unit (#2695 D).
 */
export type PriceUnit = '€/t' | '€/dt' | '€/kg' | '€/l' | 'ct/l' | '€/Stk' | '€/Einheit' | '€/ha' | '€/h' | '€/m³';
export const PRICE_UNITS: readonly PriceUnit[] = ['€/t', '€/dt', '€/kg', '€/l', 'ct/l', '€/Stk', '€/Einheit', '€/ha', '€/h', '€/m³'];

/**
 * Trade direction — ``kauf`` (the farm buys) / ``verkauf`` (the farm sells).
 */
export type PriceSide = 'kauf' | 'verkauf';
export const PRICE_SIDES: readonly PriceSide[] = ['kauf', 'verkauf'];
export const SIDE_LABELS: Record<PriceSide, string> = {
  kauf: 'Kauf',
  verkauf: 'Verkauf',
};

/**
 * Optional quality axis.
 */
export type Quality = 'konventionell' | 'bio';
export const QUALITIES: readonly Quality[] = ['konventionell', 'bio'];
export const QUALITY_LABELS: Record<Quality, string> = {
  konventionell: 'Konventionell',
  bio: 'Bio',
};

/**
 * Optional sales-channel axis (ascii ids; ``grosshandel`` displays "Großhandel").
 */
export type Channel = 'direkt' | 'grosshandel' | 'einzelhandel';
export const CHANNELS: readonly Channel[] = ['direkt', 'grosshandel', 'einzelhandel'];
export const CHANNEL_LABELS: Record<Channel, string> = {
  direkt: 'Direktvermarktung',
  grosshandel: 'Großhandel',
  einzelhandel: 'Einzelhandel',
};

/**
 * Optional price basis (#2441) — the input-price quantity axis for Saatgut /
 * Pflanzgut items. ``gewicht`` prices per Gewicht (€/dt of seed), ``einheit``
 * per Package/Saateinheit (€/Einheit, maize/beet/rape units — #2463),
 * ``pflanze`` per single plant/crown/tree (€/Stk — a plant IS one piece).
 * Present ONLY on saatgut + pflanzgut items; absent everywhere else.
 */
export type PriceBasis = 'gewicht' | 'einheit' | 'pflanze';
export const PRICE_BASES: readonly PriceBasis[] = ['gewicht', 'einheit', 'pflanze'];
export const PRICE_BASIS_LABELS: Record<PriceBasis, string> = {
  gewicht: 'je Gewicht',
  einheit: 'je Einheit',
  pflanze: 'je Pflanze',
};

/**
 * L3 — one tracked price line (item) with a stable id + its declared axes.
 */
export interface TrackedItem {
  /** Stable slug, unique across the whole catalog. */
  id: string;
  /** German display label. */
  name: string;
  unit: PriceUnit;
  /** Non-empty subset of {kauf, verkauf}. */
  sides: PriceSide[];
  /** Optional quality axis (bio/konventionell). */
  quality?: Quality[];
  /** Optional sales-channel axis. */
  channel?: Channel[];
  /** Optional price basis (#2441) — Saatgut/Pflanzgut input-price quantity axis. */
  basis?: PriceBasis;
  /**
   * Optional canonical German Kultur (Pflanzgut nach Sorten, 2026-09) — the item is
   * the planting-stock price of ONE crop, in the `by_crop` / crosswalk `crop:` spelling.
   */
  crop?: string;
  /**
   * Optional canonical bare Sorte (`crop_cycle.variety` spelling). Present on a Sorte
   * tile; absent on the crop's one „Sorte unbekannt" fallback. Requires `crop`.
   */
  variety?: string;
}

/**
 * The closed set a derived Kategorie can be derived FOR (HOF-299 S3) — the
 * `product.category` ontology values a Preisspiegel tile is minted from.
 */
export type ProductCategory = 'crop_protection' | 'fertilizer' | 'machine_work' | 'planting_material' | 'seed';
export const PRODUCT_CATEGORIES: readonly ProductCategory[] = ['crop_protection', 'fertilizer', 'machine_work', 'planting_material', 'seed'];

/**
 * L2 — a Kategorie grouping tracked items.
 *
 * HOF-299 S3 (2026-09-04): a Kategorie is `leaves: static` (default — its `items`
 * ARE the tiles) or `leaves: derived` — its tiles are the tenant's own `product`
 * entities served by `GET /twin/preisspiegel/tiles`, and `items` holds the static
 * aggregates / the one `fallbackItem` that keeps the legacy id. Tiles for exact
 * products, Sorten and Arbeitsgänge are never hardcoded here. The four derived
 * fields are present together or not at all.
 */
export interface Kategorie {
  id: string;
  name: string;
  items: TrackedItem[];
  leaves?: 'derived';
  derivedFrom?: 'product';
  productCategory?: ProductCategory;
  /** The rule set in `config/catalog/product-kategorie-rules@v1.yaml` deciding the L2. */
  typeRule?: string;
  /** One of `items` — where a fact lands when no product is known. */
  fallbackItem?: string;
}

/**
 * L1 — a Warengruppe grouping Kategorien.
 */
export interface Warengruppe {
  id: string;
  name: string;
  kategorien: Kategorie[];
}

/**
 * The v1 catalog: 10 Warengruppen, 124 tracked items.
 *
 * Counts are stated here as an orientation aid only — nothing asserts them.
 * The backend cell-budget cap derives its ceiling from this file at test time
 * rather than restating a number that rots (#2771 criterion 7).
 */
export const CATALOG: Warengruppe[] = [
  {
    id: 'saatgut',
    name: 'Saatgut',
    kategorien: [
      {
        id: 'saatgut_getreide',
        name: 'Getreide',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'seed',
        typeRule: 'saatgut_by_kulturgruppe',
        fallbackItem: 'saatgut_getreide_z',
        items: [
          { id: 'saatgut_getreide_z', name: 'Z-Saatgut Getreide', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
          { id: 'saatgut_gerste', name: 'Gerste-Saatgut', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
          { id: 'saatgut_hafer', name: 'Hafer-Saatgut', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
        ],
      },
      {
        id: 'saatgut_raps',
        name: 'Raps',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'seed',
        typeRule: 'saatgut_by_kulturgruppe',
        fallbackItem: 'saatgut_raps',
        items: [
          { id: 'saatgut_raps', name: 'Raps-Saatgut', unit: '€/Einheit', sides: ['kauf'], basis: 'einheit' },
          { id: 'saatgut_raps_gewicht', name: 'Raps-Saatgut (nach Gewicht)', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
        ],
      },
      {
        id: 'saatgut_mais',
        name: 'Mais',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'seed',
        typeRule: 'saatgut_by_kulturgruppe',
        fallbackItem: 'saatgut_mais',
        items: [
          { id: 'saatgut_mais', name: 'Mais-Saatgut', unit: '€/Einheit', sides: ['kauf'], basis: 'einheit' },
          { id: 'saatgut_mais_gewicht', name: 'Mais-Saatgut (nach Gewicht)', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
        ],
      },
      {
        id: 'saatgut_leguminosen',
        name: 'Leguminosen',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'seed',
        typeRule: 'saatgut_by_kulturgruppe',
        fallbackItem: 'saatgut_ackerbohnen',
        items: [
          { id: 'saatgut_ackerbohnen', name: 'Ackerbohnen-Saatgut', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
        ],
      },
      {
        id: 'saatgut_zwischenfruechte',
        name: 'Zwischenfrüchte',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'seed',
        typeRule: 'saatgut_by_kulturgruppe',
        fallbackItem: 'saatgut_zwischenfruechte_unbekannt',
        items: [
          { id: 'saatgut_zwischenfruechte_unbekannt', name: 'Zwischenfrucht-Saatgut (Sorte unbekannt)', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
        ],
      },
      {
        id: 'saatgut_sonstige',
        name: 'Sonstiges Saatgut',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'seed',
        typeRule: 'saatgut_by_kulturgruppe',
        fallbackItem: 'saatgut_sonstige_unbekannt',
        items: [
          { id: 'saatgut_kartoffel', name: 'Pflanzkartoffeln', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
          { id: 'saatgut_rueben', name: 'Zuckerrüben-Saatgut', unit: '€/Einheit', sides: ['kauf'], basis: 'einheit' },
          { id: 'saatgut_sonstige_unbekannt', name: 'Sonstiges Saatgut (Sorte unbekannt)', unit: '€/dt', sides: ['kauf'], basis: 'gewicht' },
        ],
      },
    ],
  },
  {
    id: 'pflanzgut',
    name: 'Pflanzgut',
    kategorien: [
      {
        id: 'pflanzgut_erdbeeren',
        name: 'Erdbeeren',
        items: [
          { id: 'erdbeer_pflanzen_agana', name: 'Agana', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Agana' },
          { id: 'erdbeer_pflanzen_allegro', name: 'Allegro', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Allegro' },
          { id: 'erdbeer_pflanzen_asia', name: 'Asia', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Asia' },
          { id: 'erdbeer_pflanzen_christina', name: 'Christina', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Christina' },
          { id: 'erdbeer_pflanzen_clery', name: 'Clery', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Clery' },
          { id: 'erdbeer_pflanzen_dahli', name: 'Dahli', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Dahli' },
          { id: 'erdbeer_pflanzen_faith', name: 'Faith', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Faith' },
          { id: 'erdbeer_pflanzen_favori', name: 'Favori', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Favori' },
          { id: 'erdbeer_pflanzen_florentina', name: 'Florentina', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Florentina' },
          { id: 'erdbeer_pflanzen_florice', name: 'Florice', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Florice' },
          { id: 'erdbeer_pflanzen_hademar', name: 'Hademar', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Hademar' },
          { id: 'erdbeer_pflanzen_karima', name: 'Karima', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Karima' },
          { id: 'erdbeer_pflanzen_lady_emma', name: 'Lady Emma', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Lady Emma' },
          { id: 'erdbeer_pflanzen_lady_grace', name: 'Lady Grace', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Lady Grace' },
          { id: 'erdbeer_pflanzen_magnum', name: 'Magnum', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Magnum' },
          { id: 'erdbeer_pflanzen_magnus', name: 'Magnus', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Magnus' },
          { id: 'erdbeer_pflanzen_malling_allure', name: 'Malling Allure', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Malling Allure' },
          { id: 'erdbeer_pflanzen_malling_centenary', name: 'Malling Centenary', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Malling Centenary' },
          { id: 'erdbeer_pflanzen_malwina', name: 'Malwina', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Malwina' },
          { id: 'erdbeer_pflanzen_ravellia', name: 'Ravellia', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Ravellia' },
          { id: 'erdbeer_pflanzen_rumba', name: 'Rumba', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Rumba' },
          { id: 'erdbeer_pflanzen_sonsation', name: 'Sonsation', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Sonsation' },
          { id: 'erdbeer_pflanzen_verdi', name: 'Verdi', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren', variety: 'Verdi' },
          { id: 'erdbeer_pflanzen', name: 'Erdbeerpflanzen (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Erdbeeren' },
        ],
      },
      {
        id: 'pflanzgut_himbeeren',
        name: 'Himbeeren',
        items: [
          { id: 'himbeer_ruten_lagorai', name: 'Lagorai', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Himbeeren', variety: 'Lagorai' },
          { id: 'himbeer_ruten_vajolet', name: 'Vajolet', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Himbeeren', variety: 'Vajolet' },
          { id: 'himbeer_ruten', name: 'Himbeerruten (Longcane, Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Himbeeren' },
        ],
      },
      {
        id: 'pflanzgut_brombeeren',
        name: 'Brombeeren',
        items: [
          { id: 'brombeer_pflanzen_sweet_royalla', name: 'Sweet Royalla', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Brombeeren', variety: 'Sweet Royalla' },
          { id: 'brombeer_pflanzen', name: 'Brombeerpflanzen (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Brombeeren' },
        ],
      },
      {
        id: 'pflanzgut_heidelbeeren',
        name: 'Heidelbeeren',
        items: [
          { id: 'heidelbeer_pflanzen', name: 'Heidelbeerpflanzen (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Heidelbeeren' },
        ],
      },
      {
        id: 'pflanzgut_spargel',
        name: 'Spargel',
        items: [
          { id: 'spargel_kronen_bacchus', name: 'Bacchus', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Bacchus' },
          { id: 'spargel_kronen_backlim', name: 'Backlim', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Backlim' },
          { id: 'spargel_kronen_cumulus', name: 'Cumulus', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Cumulus' },
          { id: 'spargel_kronen_fruehlim', name: 'Frühlim', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Frühlim' },
          { id: 'spargel_kronen_gijnlim', name: 'Gijnlim', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Gijnlim' },
          { id: 'spargel_kronen_grolim', name: 'Grolim', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Grolim' },
          { id: 'spargel_kronen_herkolim', name: 'Herkolim', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Herkolim' },
          { id: 'spargel_kronen_ramires', name: 'Ramires', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Ramires' },
          { id: 'spargel_kronen_xenolim', name: 'Xenolim', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel', variety: 'Xenolim' },
          { id: 'spargel_kronen', name: 'Spargelkronen (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Spargel' },
        ],
      },
      {
        id: 'pflanzgut_aepfel',
        name: 'Äpfel',
        items: [
          { id: 'apfel_baum', name: 'Apfelbäume (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Äpfel' },
        ],
      },
      {
        id: 'pflanzgut_kirschen',
        name: 'Kirschen',
        items: [
          { id: 'kirsch_baum', name: 'Kirschbäume (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Kirschen' },
        ],
      },
      {
        id: 'pflanzgut_reben',
        name: 'Reben',
        items: [
          { id: 'reben', name: 'Reben (Pflanzgut, Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Trauben' },
        ],
      },
      {
        id: 'pflanzgut_streuobst',
        name: 'Streuobst',
        items: [
          { id: 'streuobst_baum', name: 'Streuobstbäume (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Streuobst' },
        ],
      },
      {
        id: 'pflanzgut_miscanthus',
        name: 'Miscanthus',
        items: [
          { id: 'miscanthus_rhizom', name: 'Miscanthus-Rhizome (Sorte unbekannt)', unit: '€/Stk', sides: ['kauf'], basis: 'pflanze', crop: 'Miscanthus' },
        ],
      },
    ],
  },
  {
    id: 'duengemittel',
    name: 'Düngemittel',
    kategorien: [
      {
        id: 'duenger_mineralisch',
        name: 'Mineraldünger',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'fertilizer',
        typeRule: 'duenger_by_prefix',
        fallbackItem: 'duenger_n_kas',
        items: [
          { id: 'duenger_n_kas', name: 'N-Dünger (KAS 27)', unit: '€/t', sides: ['kauf'] },
          { id: 'duenger_p_dap', name: 'P-Dünger (DAP)', unit: '€/t', sides: ['kauf'] },
          { id: 'duenger_k_kali', name: 'K-Dünger (Kali 60)', unit: '€/t', sides: ['kauf'] },
        ],
      },
      {
        id: 'duenger_kalk',
        name: 'Kalk',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'fertilizer',
        typeRule: 'duenger_by_prefix',
        fallbackItem: 'duenger_kalk',
        items: [
          { id: 'duenger_kalk', name: 'Kohlensaurer Kalk', unit: '€/t', sides: ['kauf'] },
        ],
      },
      {
        id: 'duenger_organisch',
        name: 'Organische Dünger',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'fertilizer',
        typeRule: 'duenger_by_prefix',
        fallbackItem: 'duenger_organisch_unbekannt',
        items: [
          { id: 'duenger_organisch_unbekannt', name: 'Organische Dünger (Produkt unbekannt)', unit: '€/t', sides: ['kauf'] },
        ],
      },
      {
        id: 'duenger_blatt_spezial',
        name: 'Blatt- & Spezialdünger',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'fertilizer',
        typeRule: 'duenger_by_prefix',
        fallbackItem: 'duenger_blatt_spezial_unbekannt',
        items: [
          { id: 'duenger_blatt_spezial_unbekannt', name: 'Blatt- & Spezialdünger (Produkt unbekannt)', unit: '€/l', sides: ['kauf'] },
        ],
      },
      {
        id: 'duenger_sonstige',
        name: 'Sonstige Dünger',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'fertilizer',
        typeRule: 'duenger_by_prefix',
        fallbackItem: 'duenger_sonstige_unbekannt',
        items: [
          { id: 'duenger_sonstige_unbekannt', name: 'Sonstige Dünger (Produkt unbekannt)', unit: '€/t', sides: ['kauf'] },
        ],
      },
    ],
  },
  {
    id: 'pflanzenschutz',
    name: 'Pflanzenschutz',
    kategorien: [
      {
        id: 'psm_herbizide',
        name: 'Herbizide',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'crop_protection',
        typeRule: 'psm_by_price_group',
        fallbackItem: 'psm_herbizid',
        items: [
          { id: 'psm_herbizid', name: 'Herbizid', unit: '€/l', sides: ['kauf'] },
        ],
      },
      {
        id: 'psm_fungizide',
        name: 'Fungizide',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'crop_protection',
        typeRule: 'psm_by_price_group',
        fallbackItem: 'psm_fungizid',
        items: [
          { id: 'psm_fungizid', name: 'Fungizid', unit: '€/l', sides: ['kauf'] },
        ],
      },
      {
        id: 'psm_insektizide',
        name: 'Insektizide',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'crop_protection',
        typeRule: 'psm_by_price_group',
        fallbackItem: 'psm_insektizid',
        items: [
          { id: 'psm_insektizid', name: 'Insektizid', unit: '€/l', sides: ['kauf'] },
        ],
      },
      {
        id: 'psm_sonstige_psm',
        name: 'Sonstige Pflanzenschutzmittel',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'crop_protection',
        typeRule: 'psm_by_price_group',
        fallbackItem: 'psm_sonstige',
        items: [
          { id: 'psm_sonstige', name: 'Sonstige Pflanzenschutzmittel', unit: '€/l', sides: ['kauf'] },
        ],
      },
    ],
  },
  {
    id: 'maschinen_arbeit',
    name: 'Maschinen / Arbeit',
    kategorien: [
      {
        id: 'maschinen_ernte',
        name: 'Ernte',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_ernte_unbekannt',
        items: [
          { id: 'maschinen_ernte_unbekannt', name: 'Ernte (Arbeitsgang unbekannt)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_saat_pflanzung',
        name: 'Saat & Pflanzung',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_saat_pflanzung_unbekannt',
        items: [
          { id: 'maschinen_saat_pflanzung_unbekannt', name: 'Saat & Pflanzung (Arbeitsgang unbekannt)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_bodenbearbeitung',
        name: 'Bodenbearbeitung',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_bodenbearbeitung_unbekannt',
        items: [
          { id: 'maschinen_bodenbearbeitung_unbekannt', name: 'Bodenbearbeitung (Arbeitsgang unbekannt)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_pflege',
        name: 'Pflege & Pflanzenschutz',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_pflege_unbekannt',
        items: [
          { id: 'maschinen_pflege_unbekannt', name: 'Pflege & Pflanzenschutz (Arbeitsgang unbekannt)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_duengung',
        name: 'Düngung',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_duengung_unbekannt',
        items: [
          { id: 'maschinen_duengung_unbekannt', name: 'Düngung (Arbeitsgang unbekannt)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_transport',
        name: 'Transport',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_transport_unbekannt',
        items: [
          { id: 'maschinen_transport_unbekannt', name: 'Transport (Arbeitsgang unbekannt)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_geraete',
        name: 'Geräte & Maschinen',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_stunde',
        items: [
          { id: 'maschinen_stunde', name: 'Maschinenleistung (je Stunde)', unit: '€/h', sides: ['kauf'] },
        ],
      },
      {
        id: 'maschinen_sonstige',
        name: 'Sonstige Arbeiten',
        leaves: 'derived',
        derivedFrom: 'product',
        productCategory: 'machine_work',
        typeRule: 'maschinen_by_arbeitsgang',
        fallbackItem: 'maschinen_flaeche',
        items: [
          { id: 'maschinen_flaeche', name: 'Maschinenleistung (je Fläche)', unit: '€/ha', sides: ['kauf'] },
        ],
      },
    ],
  },
  {
    id: 'betriebsmittel',
    name: 'Betriebsmittel',
    kategorien: [
      {
        id: 'futtermittel',
        name: 'Futtermittel',
        items: [
          { id: 'futter_kraftfutter', name: 'Kraftfutter (Rind)', unit: '€/dt', sides: ['kauf'] },
          { id: 'futter_mineralfutter', name: 'Mineralfutter', unit: '€/dt', sides: ['kauf'] },
        ],
      },
      {
        id: 'heu_stroh',
        name: 'Heu / Stroh',
        items: [
          { id: 'heu_quaderballen', name: 'Heu (Quaderballen)', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'stroh_quaderballen', name: 'Stroh (Quaderballen)', unit: '€/t', sides: ['kauf', 'verkauf'] },
        ],
      },
      {
        id: 'substrate',
        name: 'Substrate',
        items: [
          { id: 'substrat', name: 'Kultursubstrat', unit: '€/m³', sides: ['kauf'] },
        ],
      },
      {
        id: 'treibstoffe',
        name: 'Treib- / Brennstoffe',
        items: [
          { id: 'diesel', name: 'Diesel', unit: 'ct/l', sides: ['kauf'] },
          { id: 'heizoel', name: 'Heizöl', unit: 'ct/l', sides: ['kauf'] },
          { id: 'adblue', name: 'AdBlue', unit: 'ct/l', sides: ['kauf'] },
        ],
      },
    ],
  },
  {
    id: 'flaechen',
    name: 'Flächen',
    kategorien: [
      {
        id: 'pacht',
        name: 'Pacht',
        items: [
          { id: 'pacht_flaeche', name: 'Pacht landwirtschaftliche Fläche', unit: '€/ha', sides: ['kauf'] },
        ],
      },
    ],
  },
  {
    id: 'marktfruechte',
    name: 'Marktfrüchte',
    kategorien: [
      {
        id: 'getreide',
        name: 'Getreide',
        items: [
          { id: 'weizen_e', name: 'Weizen E (Elite)', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'weizen_a', name: 'Weizen A (Qualität)', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'weizen_b', name: 'Weizen B (Brot)', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'weizen_futter', name: 'Futterweizen', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'gerste_futter', name: 'Futtergerste', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'gerste_brau', name: 'Braugerste', unit: '€/t', sides: ['verkauf'] },
          { id: 'roggen', name: 'Roggen (Brot)', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'hafer', name: 'Hafer (Futter)', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'koernermais', name: 'Körnermais', unit: '€/t', sides: ['verkauf'] },
          { id: 'silomais', name: 'Silomais (Frischmasse)', unit: '€/t', sides: ['kauf', 'verkauf'] },
        ],
      },
      {
        id: 'oelsaaten',
        name: 'Ölsaaten',
        items: [
          { id: 'raps', name: 'Raps', unit: '€/t', sides: ['kauf', 'verkauf'] },
          { id: 'sonnenblume', name: 'Sonnenblume', unit: '€/t', sides: ['verkauf'] },
        ],
      },
      {
        id: 'kartoffeln',
        name: 'Kartoffeln',
        items: [
          { id: 'kartoffel_festkochend', name: 'Kartoffeln festkochend', unit: '€/dt', sides: ['kauf', 'verkauf'], quality: ['konventionell', 'bio'] },
          { id: 'kartoffel_vorwiegend', name: 'Kartoffeln vorwiegend festkochend', unit: '€/dt', sides: ['kauf', 'verkauf'], quality: ['konventionell', 'bio'] },
          { id: 'kartoffel_mehligkochend', name: 'Kartoffeln mehligkochend', unit: '€/dt', sides: ['kauf', 'verkauf'], quality: ['konventionell', 'bio'] },
          { id: 'kartoffel_fontane', name: 'Kartoffeln Fontane (Verarbeitung)', unit: '€/dt', sides: ['verkauf'] },
          { id: 'kartoffel_agria', name: 'Kartoffeln Agria', unit: '€/dt', sides: ['verkauf'] },
        ],
      },
      {
        id: 'hackfruechte',
        name: 'Hackfrüchte',
        items: [
          { id: 'zuckerrueben', name: 'Zuckerrüben', unit: '€/t', sides: ['verkauf'] },
        ],
      },
      {
        id: 'leguminosen',
        name: 'Körnerleguminosen',
        items: [
          { id: 'ackerbohnen', name: 'Ackerbohnen', unit: '€/t', sides: ['verkauf'] },
        ],
      },
    ],
  },
  {
    id: 'sonderkulturen',
    name: 'Sonderkulturen',
    kategorien: [
      {
        id: 'spargel',
        name: 'Spargel',
        items: [
          { id: 'spargel_weiss_hkl1', name: 'Spargel weiß HKl I', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel'] },
          { id: 'spargel_weiss_hkl2', name: 'Spargel weiß HKl II', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel'] },
          { id: 'spargel_gruen_hkl1', name: 'Spargel grün HKl I', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel'] },
          { id: 'spargel_gruen_hkl2', name: 'Spargel grün HKl II', unit: '€/kg', sides: ['verkauf'] },
        ],
      },
      {
        id: 'beerenobst',
        name: 'Beerenobst',
        items: [
          { id: 'erdbeeren', name: 'Erdbeeren', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel', 'einzelhandel'] },
          { id: 'himbeeren', name: 'Himbeeren', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel'] },
          { id: 'brombeeren', name: 'Brombeeren', unit: '€/kg', sides: ['verkauf'], channel: ['direkt'] },
          { id: 'heidelbeeren', name: 'Heidelbeeren', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel'] },
        ],
      },
      {
        id: 'gemuese',
        name: 'Gemüse',
        items: [
          { id: 'salat_kopfsalat', name: 'Kopfsalat', unit: '€/Stk', sides: ['verkauf'] },
          { id: 'moehren', name: 'Möhren', unit: '€/dt', sides: ['verkauf'] },
          { id: 'zwiebeln', name: 'Speisezwiebeln', unit: '€/dt', sides: ['kauf', 'verkauf'] },
        ],
      },
      {
        id: 'obst',
        name: 'Obst',
        items: [
          { id: 'aepfel_tafel', name: 'Tafeläpfel', unit: '€/dt', sides: ['verkauf'] },
          { id: 'birnen', name: 'Birnen', unit: '€/dt', sides: ['verkauf'] },
        ],
      },
      {
        id: 'steinobst',
        name: 'Steinobst',
        items: [
          { id: 'kirschen', name: 'Kirschen', unit: '€/kg', sides: ['verkauf'], channel: ['direkt', 'grosshandel'] },
        ],
      },
    ],
  },
  {
    id: 'tierhaltung',
    name: 'Tierhaltung',
    kategorien: [
      {
        id: 'milch',
        name: 'Milch',
        items: [
          { id: 'milch_konv', name: 'Milch konventionell', unit: 'ct/l', sides: ['verkauf'], quality: ['konventionell'] },
          { id: 'milch_bio', name: 'Milch bio', unit: 'ct/l', sides: ['verkauf'], quality: ['bio'] },
        ],
      },
      {
        id: 'vieh',
        name: 'Vieh',
        items: [
          { id: 'mastschwein', name: 'Mastschwein', unit: '€/kg', sides: ['verkauf'] },
          { id: 'ferkel', name: 'Ferkel', unit: '€/Stk', sides: ['kauf', 'verkauf'] },
          { id: 'mastbulle', name: 'Mastbulle', unit: '€/kg', sides: ['verkauf'] },
          { id: 'kuh_schlacht', name: 'Schlachtkuh', unit: '€/kg', sides: ['verkauf'] },
        ],
      },
    ],
  },
];
